import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"

type Dimensions = { width: number; height: number }

export function ParentSize({
	children,
	className,
}: {
	className?: string
	children(dimensions: Dimensions): React.ReactElement | null
}) {
	const ref = useRef<HTMLDivElement>(null!)
	const [dimensions, setDimensions] = useState<Dimensions>({
		width: 0,
		height: 0,
	})

	const updateDimensions = useCallback(() => {
		if (ref.current) {
			const { width, height } = ref.current.getBoundingClientRect()
			const w = Math.round(width)
			const h = Math.round(height)
			setDimensions((prev) => {
				if (prev.width === w && prev.height === h) return prev
				return { width: w, height: h }
			})
		}
	}, [])

	useEffect(() => {
		const observer = new ResizeObserver(updateDimensions)
		if (ref.current) {
			observer.observe(ref.current)
		}
		updateDimensions()

		// Safari can report 0 height on initial layout when parent height is
		// inherited via flexbox. Re-measure after a frame to pick up the
		// correct dimensions once layout has settled.
		const rafId = requestAnimationFrame(updateDimensions)

		return () => {
			observer.disconnect()
			cancelAnimationFrame(rafId)
		}
	}, [updateDimensions])

	return (
		<div
			ref={ref}
			className={cn("h-full min-h-0 w-full min-w-0", className)}
		>
			{children(dimensions)}
		</div>
	)
}
